// Form state -> EKRM payload, extracted from CreateEkrm.vue so a second wizard can produce
// byte-identical documents without touching the original one.
//
// Everything downstream of the "Create Reference Model" button belongs to the Python simulator:
// createEkrm (store/modules/manager.js) writes `definition` straight to ekrm/{id} and buildEkrm
// (functions/index.js) hands the VM nothing but the doc id, so no JS we control validates the
// shape. That makes this a deliberate duplication of logic that still lives inline in
// CreateEkrm.vue: the old wizard was to be left exactly as it is, so it keeps its own copy.
// Migrating it onto this module is a safe follow-up, not part of this change.
//
// Two conventions the whole file depends on, both inherited from jspreadsheet:
//   * every grid cell is a STRING, including the numbers. buildEkrmDefinition slices them out
//     untouched, so the payload carries strings exactly where the old one did.
//   * a row is [label, ...values]; the label of an own-controllable / saturation row is the
//     medium name and becomes the KEY in the payload, so it is never translated.

import { gridSpends } from '@/utils/hillCurve.js'

// The ten grid positions as multiples of the saturation point, which is what both the jspreadsheet
// formulas and the payload's `spend` array are built from. Taken from gridSpends so the ladder has
// one definition: the cells the user reads and the numbers the EKRM is sent cannot drift apart.
const GRID_COEFFICIENTS = gridSpends(1)

// gridSpends floors a non-positive point at 1 so a chart always has an axis to draw on; the payload
// would rather carry the ten zeros the old wizard's arithmetic produced, since validateEkrmForm
// blocks the save on a saturation point of zero anyway.
function saturationSpends (cell) {
  const point = Number(cell)
  if (!isFinite(point) || point <= 0) return GRID_COEFFICIENTS.map(function () { return 0 })
  return gridSpends(point)
}

const MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december'
]

// The months a grid carries when nobody says otherwise. A clone of a model with more than one
// complete calendar year widens every monthly grid to 12*N instead — the EKRM accepts a payload of
// N consecutive calendar years, and the wizard that builds one passes the window in. Everything
// downstream of the grids reads `row.slice(2)` and so follows the width on its own; the only rule
// is that the KPI, seasonality and both spend grids stay the SAME width as each other, which is why
// the count is decided once here and handed to all of them.
const MONTHS_PER_YEAR = 12

// Column count of a monthly grid: label + variation range + the months.
function monthlyRowLength (monthCount) {
  return 2 + (monthCount || MONTHS_PER_YEAR)
}

function emptyMonthlyRow (label, monthCount) {
  const row = [label]
  const length = monthlyRowLength(monthCount)
  for (let i = 1; i < length; i++) {
    row.push('')
  }
  return row
}

function monthColumns (customFormat, allowNegatives, monthCount) {
  const count = monthCount || MONTHS_PER_YEAR
  const columns = []
  for (let i = 0; i < count; i++) {
    const column = {
      type: 'numeric',
      title: MONTH_KEYS[i % MONTHS_PER_YEAR],
      width: '100px',
      decimal: '.',
      allowEmpty: true,
      customFormat: customFormat
    }
    if (allowNegatives) {
      column.allowNegatives = true
    }
    columns.push(column)
  }
  return columns
}

// The translated title of each month column. With no window it is the twelve month names on their
// own, which is what a calendar-averaged year is: a typical January, not any particular one. With a
// window of 'YYYY-MM' keys the year rides along, because "January" three times over would name
// three different columns identically. Reuses the twelve keys that already exist rather than
// inventing one per month of an arbitrarily long window.
export function monthTitlesFor (t, months) {
  if (!months || months.length === 0) {
    return MONTH_KEYS.map(function (key) { return t(key) })
  }
  return months.map(function (key) {
    const month = Number(String(key).slice(5, 7))
    const name = t(MONTH_KEYS[(month - 1 + MONTHS_PER_YEAR) % MONTHS_PER_YEAR])
    return name + ' ' + String(key).slice(0, 4)
  })
}

// The grids a wizard starts from, with the same column definitions, widths, formats and disabled
// rows the old wizard declares in data(). `t` receives a bare key and is expected to resolve it
// inside the component's own i18n namespace.
//
// `months` is an optional window of 'YYYY-MM' keys. Omitted — which is how the old wizard calls
// this — every monthly grid is the twelve columns it has always been, titled with the bare month
// names, so the grids they build are unchanged down to the string.
export function createGrids (t, months) {
  const label = function (key) { return t(key) }
  const monthTitles = monthTitlesFor(label, months)
  const monthCount = monthTitles.length
  const applyMonthTitles = function (columns) {
    for (let i = 0; i < monthCount; i++) {
      columns[i + 2].title = monthTitles[i]
    }
    return columns
  }
  return {
    futureTrends: {
      columns: [
        { type: 'html', title: ' ', width: '450px', readOnly: true, wordWrap: true, align: 'left' },
        { type: 'numeric', title: label('min'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true },
        { type: 'numeric', title: label('max'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegatives: true }
      ],
      data: [
        [label('salesRevenue'), '', ''],
        [label('ownControllableMediaSpendTotal'), '', ''],
        [label('ownNonControllableMediaSpendTotal'), '', ''],
        [label('competitionSpendTotal'), '', '']
      ]
    },
    attributions: {
      columns: [
        { type: 'html', title: ' ', width: '450px', readOnly: true, wordWrap: true, align: 'left' },
        { type: 'numeric', title: label('min'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegativesRows: ['2'] },
        { type: 'numeric', title: label('max'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%', allowNegativesRows: ['2'] }
      ],
      data: [
        [label('ownControllableMediaSpendTotal'), '', ''],
        [label('ownNonControllableMediaSpendTotal'), '', ''],
        [label('competitionSpendTotal'), '', ''],
        [label('periodicCyclicEvents'), '', ''],
        [label('spotEvents'), '', ''],
        [label('base'), '', '']
      ],
      disabledRows: [3, 4, 5]
    },
    seasonalityAndSpotEvents: {
      columns: applyMonthTitles([
        { type: 'html', title: ' ', width: '350px', readOnly: true, wordWrap: true, align: 'left' },
        { type: 'numeric', title: label('variationRangePercent'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' }
      ].concat(monthColumns('%', true, monthCount))),
      data: [
        emptyMonthlyRow(label('percentSalesAttributedPeriodic'), monthCount),
        emptyMonthlyRow(label('percentSalesAttributedSpot'), monthCount)
      ]
    },
    kpi: {
      columns: applyMonthTitles([
        { type: 'html', title: ' ', width: '250px', readOnly: true, wordWrap: true, align: 'left' },
        { type: 'numeric', title: label('variationRangePercent'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' }
      ].concat(monthColumns('$', false, monthCount))),
      data: [
        emptyMonthlyRow(label('avgMonthlyRevenue'), monthCount)
      ]
    },
    saturation: {
      columns: [
        { type: 'html', title: label('media'), width: '200px', readOnly: true, wordWrap: true, align: 'left' },
        { type: 'numeric', title: label('revenueVariationRange'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' },
        { type: 'numeric', title: label('maximumSpend'), width: '120px', decimal: '.', allowEmpty: true, customFormat: '$' },
        { type: 'numeric', title: label('saturationPoint'), width: '120px', decimal: '.', allowEmpty: true, customFormat: '$' }
      ].concat([1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(function (n) {
        return { type: 'numeric', title: String(n), width: '100px', decimal: '.', allowEmpty: true, customFormat: '$' }
      })),
      mergeCells: {},
      disabledRows: [],
      data: []
    },
    mediaSpend: {
      ownControllable: {
        columns: applyMonthTitles([
          { type: 'html', title: label('ownControllableMedia'), width: '200px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: label('variationRangePercent'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' }
        ].concat(monthColumns('$', false, monthCount))),
        data: []
      },
      otherSpend: {
        columns: applyMonthTitles([
          { type: 'html', title: label('other'), width: '200px', readOnly: true, wordWrap: true, align: 'left' },
          { type: 'numeric', title: label('variationRangePercent'), width: '200px', decimal: '.', allowEmpty: true, customFormat: '%' }
        ].concat(monthColumns('$', false, monthCount))),
        data: [
          emptyMonthlyRow(label('ownNonControllableMediaTotal'), monthCount),
          emptyMonthlyRow(label('competitionTotal'), monthCount)
        ]
      }
    }
  }
}

export function defaultDecayEntry () {
  return { maximumEffect: [0, 2], halfLife: [0, 3] }
}

// The longest a medium's name may be. Not a limit the payload imposes — nothing downstream reads
// one — but the width a name is READ at: the row label of three grids, the legend of two charts and
// a card about a hundred pixels wide, none of which can show more than this and all of which
// silently ellipse what is over it.
export const MEDIUM_NAME_MAX = 60

// A medium's name as the payload will carry it: whitespace collapsed, trimmed, and cut to the
// length above. Normalising BEFORE the name is compared and before it is stored is what makes the
// comparison mean anything — the name is the KEY of three maps of the definition, so two names a
// run of spaces apart are one key wearing two faces, and the second would overwrite the first.
export function mediumName (raw) {
  const text = raw === null || raw === undefined ? '' : String(raw)
  return text.replace(/\s+/g, ' ').trim().slice(0, MEDIUM_NAME_MAX)
}

// Why a name cannot be used, or '' when it can. Codes rather than sentences: the caller owns the
// words, this owns the rule.
//
// The comparison is case-insensitive and that is a choice rather than a defence. `Search` and
// `search` really are two distinct keys, so a payload built from both is valid and carries two rows
// nobody can tell apart on the report that comes back — which is worse than being refused here.
export function mediumNameError (raw, taken) {
  const name = mediumName(raw)
  if (!name) return 'empty'
  const lower = name.toLowerCase()
  const used = taken || []
  for (let index = 0; index < used.length; index++) {
    if (mediumName(used[index]).toLowerCase() === lower) return 'taken'
  }
  return ''
}

// Rebuilds the per-medium rows when the media selection changes, preserving whatever the user
// already typed for media that stay selected. Mirrors the `mediaInternal` watcher of the old
// wizard, including the jspreadsheet formulas that render the saturation spend grid off the
// saturation point (column D) and the merge/disable maps that pair the two rows of each medium.
//
// `monthCount` is the width of a new spend row. It defaults to twelve, so the two wizards that
// build a one-year grid call this exactly as they always have; a clone of a multi-year model passes
// its own count, and a medium that was already in the grid keeps the row it had either way.
export function syncMediaGrids (media, current, monthCount) {
  const previousSpend = (current && current.mediaSpend && current.mediaSpend.ownControllable.data) || []
  const previousSaturation = (current && current.saturation && current.saturation.data) || []
  const previousDecay = (current && current.decay) || {}
  const previousMin = (current && current.minHalfLife) || {}
  const previousMax = (current && current.maxHalfLife) || {}

  const spendData = []
  const saturationData = []
  const mergeCells = {}
  const disabledRows = []
  const decay = {}
  const minHalfLife = {}
  const maxHalfLife = {}

  let row = 1
  media.forEach(function (medium) {
    const existingSpend = previousSpend.find(function (r) { return r[0] === medium })
    spendData.push(existingSpend ? existingSpend.slice() : emptyMonthlyRow(medium, monthCount))

    let existingHeader = null
    let existingRevenue = null
    for (let i = 0; i < previousSaturation.length; i += 2) {
      if (previousSaturation[i] && previousSaturation[i][0] === medium) {
        existingHeader = previousSaturation[i]
        existingRevenue = previousSaturation[i + 1]
        break
      }
    }
    // Each column reads the saturation point directly rather than adding a step to the column
    // before it, as the old wizard's running chain does: the ladder is geometric now, and ten
    // independent formulas cannot accumulate a rounding the way a chain of ten additions can.
    saturationData.push([
      medium,
      existingHeader ? existingHeader[1] : '',
      existingHeader ? existingHeader[2] : '',
      existingHeader ? existingHeader[3] : ''
    ].concat(GRID_COEFFICIENTS.map(function (coefficient) {
      return `=D${row}*${coefficient}`
    })))
    // Fourteen cells, and deliberately NOT a monthly row: four leading columns plus the ten grid
    // positions. It happens to be the width a twelve-month row also has, which is the one trap in
    // this file — the saturation grid never widens with the months.
    saturationData.push(existingRevenue ? existingRevenue.slice() : ['', '', '', '', '', '', '', '', '', '', '', '', '', ''])

    mergeCells[`A${row}`] = [1, 2]
    mergeCells[`B${row}`] = [1, 2]
    mergeCells[`C${row}`] = [1, 2]
    mergeCells[`D${row}`] = [1, 2]
    disabledRows.push(row - 1)
    row += 2

    decay[medium] = previousDecay[medium] ? previousDecay[medium] : defaultDecayEntry()
    minHalfLife[medium] = previousMin[medium] !== undefined ? previousMin[medium] : decay[medium].maximumEffect[0]
    // Same rule an imported channel is given: a month past the peak, opened up if the half-life
    // already held sits beyond it.
    maxHalfLife[medium] = previousMax[medium] !== undefined
      ? previousMax[medium]
      : Math.max(decay[medium].maximumEffect[1] + 1, decay[medium].halfLife[1])
  })

  return {
    spendData: spendData,
    saturationData: saturationData,
    saturationMergeCells: mergeCells,
    saturationDisabledRows: disabledRows,
    decay: decay,
    minHalfLife: minHalfLife,
    maxHalfLife: maxHalfLife
  }
}

// Largest monthly spend per medium — the Maximum Spend column of the saturation grid, and the
// yardstick the saturation-point check uses. Indexed by medium, not by saturation row.
export function maxMonthlySpends (ownControllableRows) {
  return (ownControllableRows || []).map(function (row) {
    return Math.max.apply(null, row.slice(2).map(Number))
  })
}

// Periodic, Spot and Base attribution rows are derived, never typed: periodic/spot are the
// KPI-weighted average of their monthly vectors give or take the variation range, and Base is
// whatever is left of 100%. Port of updatedAttributionsHandler.
//
// Returns the values rather than writing them, for two reasons: assigning into a row array by
// index is not reactive in Vue 2, and the caller needs to compare before it writes — the grid
// only repaints when the whole data array is replaced, so a blind write on every keystroke would
// rebuild the table under the user's cursor.
//
// One deliberate difference from the old wizard: it summed Base off the periodic and spot rows
// BEFORE recomputing them, so Base always described the edit before last. Here Base is computed
// last, off the rows this same call just produced.
export function derivedAttributionRows (form) {
  const attributions = form.attributions.data
  const kpiVector = form.kpi.data[0].slice(2).map(Number)
  const sumKpi = kpiVector.reduce(function (r, a) { return r + a }, 0)

  const weightedShare = function (rowIndex) {
    const vector = form.seasonalityAndSpotEvents.data[rowIndex].slice(2).map(Number)
    const range = Number(form.seasonalityAndSpotEvents.data[rowIndex][1])
    const weighted = kpiVector.reduce(function (r, a, i) { return r + a * vector[i] }, 0) / sumKpi
    return {
      min: isNaN(weighted - range) ? '0' : Math.round(weighted - range).toString(),
      max: isNaN(weighted + range) ? '0' : Math.round(weighted + range).toString()
    }
  }
  const periodic = weightedShare(0)
  const spot = weightedShare(1)

  // Base closes the gap to 100%, and the old wizard deliberately crosses the bounds: the base
  // minimum is 100 minus the sum of the maxima, and vice versa.
  const sumMax = Number(attributions[0][2]) + Number(attributions[1][2]) + Number(attributions[2][2]) +
    Number(periodic.max) + Number(spot.max)
  const sumMin = Number(attributions[0][1]) + Number(attributions[1][1]) + Number(attributions[2][1]) +
    Number(periodic.min) + Number(spot.min)
  const base = {
    min: isNaN(sumMax) ? '0' : Math.round(100 - Math.min(100, sumMax)).toString(),
    max: isNaN(sumMin) ? '0' : Math.round(100 - Math.max(0, sumMin)).toString()
  }

  return { periodic: periodic, spot: spot, base: base }
}

// The `definition` map written to ekrm/{id}. CreateEkrm.saveJSON's object, with the four
// whole-model sections written once instead of re-assigned on every pass of the per-medium loop —
// the same result for any model with at least one medium, and zero media is already a hard
// validation error — plus the per-medium `x`/`y` curves at the end of the loop, which the old
// wizard has no source model to produce and so never sends.
export function buildEkrmDefinition (form) {
  const obj = {
    reference: form.reference,
    media_spend: {
      own_controllable: {},
      other: {
        own_non_controllable_and_comarketing: {
          variation_range: form.mediaSpend.otherSpend.data[0][1],
          spend: form.mediaSpend.otherSpend.data[0].slice(2)
        },
        competition: {
          variation_range: form.mediaSpend.otherSpend.data[1][1],
          spend: form.mediaSpend.otherSpend.data[1].slice(2)
        }
      }
    },
    decay: {},
    saturation: {},
    kpi: {
      avg_monthly_revenue: {
        variation_range: form.kpi.data[0][1],
        revenue: form.kpi.data[0].slice(2)
      }
    },
    seasonality_and_spot_events: {
      periodic_cyclic_events_attribution: {
        variation_range: form.seasonalityAndSpotEvents.data[0][1],
        attribution: form.seasonalityAndSpotEvents.data[0].slice(2)
      },
      spot_events_attribution: {
        variation_range: form.seasonalityAndSpotEvents.data[1][1],
        attribution: form.seasonalityAndSpotEvents.data[1].slice(2)
      }
    },
    attributions: {
      own_controllable_media_spend: { min: form.attributions.data[0][1], max: form.attributions.data[0][2] },
      own_non_controllable_and_comarketing_media_spend: { min: form.attributions.data[1][1], max: form.attributions.data[1][2] },
      competition_media_spend: { min: form.attributions.data[2][1], max: form.attributions.data[2][2] },
      periodic_cyclic_events: { min: form.attributions.data[3][1], max: form.attributions.data[3][2] },
      spot_events: { min: form.attributions.data[4][1], max: form.attributions.data[4][2] },
      base: { min: form.attributions.data[5][1], max: form.attributions.data[5][2] }
    },
    future_trends: {
      sales: { min: form.futureTrends.data[0][1], max: form.futureTrends.data[0][2] },
      own_controllable_media_spend: { min: form.futureTrends.data[1][1], max: form.futureTrends.data[1][2] },
      own_non_controllable_and_comarketing_media_spend: { min: form.futureTrends.data[2][1], max: form.futureTrends.data[2][2] },
      competition_media_spend: { min: form.futureTrends.data[3][1], max: form.futureTrends.data[3][2] }
    }
  }
  for (let i = 0; i < form.reference.media.length; i++) {
    const medium = form.reference.media[i]
    obj.media_spend.own_controllable[form.mediaSpend.ownControllable.data[i][0]] = {
      variation_range: form.mediaSpend.ownControllable.data[i][1],
      spend: form.mediaSpend.ownControllable.data[i].slice(2)
    }
    obj.decay[medium] = {
      months_to_maximum_effect: {
        min: form.decay[medium].maximumEffect[0],
        max: form.decay[medium].maximumEffect[1]
      },
      months_to_half_life: {
        min: form.decay[medium].halfLife[0],
        max: form.decay[medium].halfLife[1]
      }
    }
    // Two rows per medium: the header carries the saturation point, the one below it the curve.
    const j = 2 * i
    // The saturation block keys on the grid's row label and the decay block on the medium — the
    // two are the same string for every grid syncMediaGrids builds, but they are read from
    // different places and neither is derived from the other, so the curve below is written to
    // whichever key its own block ended up using.
    const saturationKey = form.saturation.data[j][0]
    obj.saturation[saturationKey] = {
      variation_range: form.saturation.data[j][1],
      saturation_point: form.saturation.data[j][3],
      // Regenerated from the saturation point rather than read back out of the formula cells, as
      // the old wizard does — but off the geometric ladder, which is where this payload stops
      // being byte-identical to the one CreateEkrm.vue writes. The EKRM reads `spend` and
      // `revenue` and never the saturation point, so the positions are free; a clone reopened in
      // the old wizard will regenerate them evenly again on its next edit.
      spend: saturationSpends(form.saturation.data[j][3]),
      revenue: form.saturation.data[j + 1].slice(4)
    }

    // The source model's own curves, for a medium still describing the channel it was imported
    // from. The EKRM fits these directly and ignores that medium's expert fields in whichever of
    // the two blocks carries one, so a curve is only sent while the wizard's own numbers still
    // agree with it: a wizard drops the saturation half the moment a slider or a grid cell moves
    // and the decay half the moment a range does, and each medium and each block is decided on
    // its own. The legacy keys stay beside the curve regardless, because check_ekrm_correctness
    // reads months_to_half_life and spend straight off the raw payload and throws on a document
    // that carries only a curve.
    const curves = (form.curves && form.curves[medium] && form.curves[medium].source) || {}
    if (curves.decay) {
      obj.decay[medium].x = curves.decay.x.slice()
      obj.decay[medium].y = curves.decay.y.slice()
    }
    if (curves.saturation) {
      obj.saturation[saturationKey].x = curves.saturation.x.slice()
      obj.saturation[saturationKey].y = curves.saturation.y.slice()
    }
  }
  return obj
}

// The JSON string the clone button rehydrates into the OLD wizard, so the ten keys and their
// shapes have to stay exactly as CreateEkrm.created() expects them. `extra` rides along for
// provenance; created() reads only the known keys and ignores anything else.
export function buildEkrmOriginalDefinition (form, extra) {
  return JSON.stringify(Object.assign({
    reference: form.reference,
    futureTrends: form.futureTrends,
    attributions: form.attributions,
    seasonalityAndSpotEvents: form.seasonalityAndSpotEvents,
    kpi: form.kpi,
    saturation: form.saturation,
    decay: form.decay,
    mediaSpend: form.mediaSpend,
    minHalfLife: form.minHalfLife,
    maxHalfLife: form.maxHalfLife
  }, extra || {}))
}

function isBlank (value) {
  return value === '' || value === null || value === undefined || isNaN(Number(value))
}

// Port of CreateEkrm.validateAll. Messages stay in English, as they are in the old wizard, so
// both dialogs read the same. `sectionOffset` shifts the section numbers the messages point at,
// because a wizard that inserts a "Channel sources" step ahead of them needs an offset of 1 for
// the numbers to still name the right step.
//
// Every warning also carries a `code`. A wizard hides the handful of warnings that are its own
// design rather than anything a user could act on — a saturation grid deliberately placed
// past the planned spend, co-marketing cells a model report cannot fill — and matching them on the
// English text above would break the moment one of these sentences is reworded. Errors carry none:
// they are all shown, all of the time. The two dialogs that render these read `type`, `sections` and
// `text` only, so this is additive.
export function validateEkrmForm (form, sectionOffset) {
  const offset = sectionOffset || 0
  const errors = []
  const warnings = []
  const at = function (sections) { return sections.map(function (s) { return s > 2 ? s + offset : s }) }
  const error = function (sections, text) { errors.push({ type: 'error', sections: at(sections), text: text }) }
  const warn = function (sections, code, text) {
    warnings.push({ type: 'warning', sections: at(sections), code: code, text: text })
  }

  if (!form.reference.category || form.reference.category.length === 0) {
    error([2], 'Model name is required.')
  }
  if (!form.reference.market || form.reference.market.length === 0) {
    error([2], 'Market is required.')
  }

  const totalKPI = form.kpi.data[0].slice(2).reduce(function (sum, a) { return Number(sum) + Number(a) }, 0)
  let totalMediaSpendOwn = 0
  for (const medium of form.mediaSpend.ownControllable.data) {
    totalMediaSpendOwn += medium.slice(2).reduce(function (sum, a) { return Number(sum) + Number(a) }, 0)
  }
  if (totalKPI <= totalMediaSpendOwn) {
    warn([3, 6], 'revenueBelowSpend', 'Sales must be greater than media spend.')
  }

  const attributionLabels = [
    'media spend',
    'co-marketing',
    'competition'
  ]
  attributionLabels.forEach(function (label, index) {
    const row = form.attributions.data[index]
    if (row[2] === '' || row[1] === '') return
    const min = Number(row[1])
    const max = Number(row[2])
    // Relative, where this used to ask for a flat three points. A component attributed 4% of sales
    // cannot be given a three-point band without that band meaning plus or minus 35%, and the EKRM
    // only corrects an attribution that falls OUTSIDE the range it is handed — so every point of
    // width is a point in which the model it builds is free to disagree with the numbers it was
    // given. What is worth warning about is the other end: a band so narrow that the EKRM's five
    // rescaling passes cannot land inside it.
    const floor = Math.max(0.05 * Math.abs((max + min) / 2), 0.1)
    if (Math.abs(max - min) < floor) {
      warn([8], 'attributionBandNarrow', `The attribution range for ${label} is too narrow.`)
    }
  })
  if (form.attributions.data[2][2] !== '' && form.attributions.data[2][1] !== '' &&
      (Number(form.attributions.data[2][2]) > 0 || Number(form.attributions.data[2][1]) > 0)) {
    error([8], 'Competition attribution cannot be positive.')
  }

  const maxSpends = maxMonthlySpends(form.mediaSpend.ownControllable.data)
  for (let index = 0; index < form.reference.media.length; index++) {
    let medium = form.saturation.data[2 * index]
    if (isBlank(medium[1]) || isBlank(medium[3])) {
      error([5], `${form.reference.media[index]} has no saturation data.`)
    }
    medium = form.saturation.data[2 * index + 1]
    if (medium.slice(4).some(isBlank)) {
      error([5], `${form.reference.media[index]} has no saturation curve.`)
    }
    medium = form.mediaSpend.ownControllable.data[index]
    if (isBlank(medium[1])) {
      error([3], `${medium[0]} has no variation range.`)
    }
    if (medium.slice(2).every(isBlank)) {
      error([3], `${medium[0]} is missing monthly spend.`)
    } else if (medium.slice(2).some(isBlank)) {
      warn([3], 'spendMonthsMissing', `${medium[0]} is missing monthly spend; empty months will be set to zero.`)
    }
  }

  for (const medium of form.mediaSpend.otherSpend.data) {
    if (medium.slice(2).some(function (c) { return !isBlank(c) }) && medium.slice(2).some(isBlank)) {
      warn([3], 'spendMonthsMissing', `${medium[0]} is missing monthly spend; empty months will be set to zero.`)
      if (isBlank(medium[1])) {
        error([3], `${medium[0]} has no variation range.`)
      }
    }
  }

  // A component only has to be complete if it is used at all: declared in Media Spend, in
  // Attributions or in Future Trends. Once it appears anywhere, every sheet that misses it is
  // called out, because the simulator would otherwise silently drop or invent it.
  const otherComponents = [
    { name: 'Co-marketing', spendRow: 0, attributionRow: 1, trendRow: 2, missingRange: 'between 0% and 100%' },
    { name: 'Competition', spendRow: 1, attributionRow: 2, trendRow: 3, missingRange: 'between -100% and 0%' }
  ]
  otherComponents.forEach(function (component) {
    const missingInSpend = form.mediaSpend.otherSpend.data[component.spendRow].slice(1).some(isBlank)
    const missingInAttributions = form.attributions.data[component.attributionRow].slice(1).some(isBlank)
    const missingInTrends = form.futureTrends.data[component.trendRow].slice(1).some(isBlank)
    if (!missingInSpend || !missingInAttributions || !missingInTrends) {
      if (missingInSpend) {
        warn([3], 'otherMissingSpend', `${component.name} has no spend and will be ignored.`)
      }
      if (missingInAttributions) {
        warn([8], 'otherMissingAttributions', `${component.name} has no attribution; the model will choose one ${component.missingRange}.`)
      }
      if (missingInTrends) {
        warn([9], 'otherMissingTrends', `${component.name} has no future trend; the model will choose one ${component.missingRange}.`)
      }
    }
  })

  for (const item of form.mediaSpend.ownControllable.data) {
    if (Number(item[1]) >= 50) {
      warn([3], 'spendVariationRangeHigh', 'Media spend variation ranges should be under 50%.')
      break
    }
  }
  for (const item of form.mediaSpend.otherSpend.data) {
    if (Number(item[1]) >= 50) {
      warn([3], 'otherVariationRangeHigh', 'Other spend variation ranges should be under 50%.')
      break
    }
  }
  for (const [index, item] of form.saturation.data.entries()) {
    if (index % 2 === 0 && Number(item[1]) >= 50) {
      warn([5], 'saturationVariationRangeHigh', 'Saturation variation ranges should be under 50%.')
      break
    }
  }

  // The saturation point has to sit in the same order of magnitude as the spend the grid will be
  // asked about. maxSpends is indexed by medium, so the header row at 2*i pairs with maxSpends[i]
  // — the old wizard indexed it by saturation row, which read the wrong medium's spend from the
  // second medium onwards.
  for (const [index, item] of form.saturation.data.entries()) {
    if (index % 2 !== 0) continue
    const maxSpend = maxSpends[index / 2]
    const saturationPoint = Number(item[3])
    if (saturationPoint === 0) {
      error([5], `The saturation point of ${item[0]} must be greater than 0.`)
      break
    }
    if (2 * maxSpend < saturationPoint || maxSpend > 2 * saturationPoint) {
      warn([5], 'saturationPointBand', `The saturation point of ${item[0]} should be between half and double its highest monthly spend.`)
      break
    }
  }

  if (Number(form.kpi.data[0][1]) >= 50) {
    warn([6], 'kpiVariationRangeHigh', 'The sales variation range should be under 50%.')
  } else if (isBlank(form.kpi.data[0][1])) {
    error([6], `${form.kpi.data[0][0]} has no variation range.`)
  }

  let sumAttributions = 0
  const fixedComponents = ['Base', 'Spot Events', 'Periodic/Cyclic Events']
  for (const [index, component] of form.attributions.data.entries()) {
    // Row 5 is Base; it is derived and excluded from the sum. Comparing by index rather than by
    // the label keeps this working when the labels are translated.
    if (index !== 5) {
      sumAttributions += Number(component[1])
    }
    const isDerived = index >= 3 || fixedComponents.includes(component[0])
    if (!isDerived && (component[1] !== '') && (component[1] === component[2])) {
      error([8], `The attribution min and max of ${component[0]} cannot be equal.`)
    }
    if (!isDerived && ((component[1] === '') !== (component[2] === ''))) {
      error([8], `Fill in both the attribution min and max of ${component[0]}, or neither.`)
    }
  }
  if (sumAttributions >= 100) {
    error([8], 'The attribution minimums (except base) must add up to less than 100%.')
  }
  if (Number(form.attributions.data[0][1]) <= 0 || Number(form.attributions.data[0][2]) <= 0) {
    error([8], 'Marketing attribution must be positive.')
  }
  for (const component of form.attributions.data) {
    if (Number(component[2]) < Number(component[1])) {
      error([8], 'Attribution minimums cannot exceed their maximums.')
      break
    }
  }

  for (const [index, medium] of form.saturation.data.entries()) {
    if (index % 2 !== 1) continue
    let prev = -1
    for (const value of medium.slice(4)) {
      if (Number(value) < prev) {
        error([5], `The saturation curve of ${form.saturation.data[index - 1][0]} cannot decrease.`)
        break
      }
      prev = Number(value)
    }
    // The curve has to have flattened by the end of the grid, otherwise the grid stops short of
    // the channel's saturation and the model has nothing to extrapolate from.
    const last = Number(medium[13])
    const beforeLast = Number(medium[12])
    if ((last - beforeLast) / last >= 0.03) {
      warn([5], 'saturationClimbing', `The saturation curve of ${form.saturation.data[index - 1][0]} should flatten out by the end.`)
    }
  }

  const seasonalityRows = [
    { index: 0, label: 'Seasonality' },
    { index: 1, label: 'Spot events' }
  ]
  seasonalityRows.forEach(function (row) {
    for (const value of form.seasonalityAndSpotEvents.data[row.index].slice(1)) {
      if (Math.abs(Number(value)) > 100) {
        error([7], `${row.label} values must be between -100% and 100%.`)
        break
      }
    }
    if (form.seasonalityAndSpotEvents.data[row.index].slice(2).some(function (c) { return !isBlank(c) })) {
      if (isBlank(form.seasonalityAndSpotEvents.data[row.index][1])) {
        error([7], `${form.seasonalityAndSpotEvents.data[row.index][0]} has no variation range.`)
      }
    }
  })
  for (let index = 2; index < form.seasonalityAndSpotEvents.data[0].length; index++) {
    if (Math.abs(Number(form.seasonalityAndSpotEvents.data[0][index]) + Number(form.seasonalityAndSpotEvents.data[1][index])) > 100) {
      error([7], 'Sum of Periodic/Cyclic + Spot Events must be between -100% and 100% for each month.')
      break
    }
  }

  // A medium with no spend at all is allowed: ngtorch scales an all-zero column to one, and a
  // channel that was not bought still carries the curve its model measured.
  for (const medium of form.mediaSpend.ownControllable.data) {
    for (const value of medium.slice(2)) {
      if (Number(value) < 0) {
        error([3], `Media Spend values for ${medium[0]} must be non-negative.`)
      }
    }
  }
  for (const medium of form.mediaSpend.otherSpend.data) {
    let hasData = false
    let total = 0
    for (const value of medium.slice(2)) {
      if (value !== '') {
        hasData = true
      }
      const val = Number(value)
      total += val
      if (val < 0) {
        error([3], `Media Spend values for ${medium[0]} must be non-negative.`)
      }
    }
    if (hasData && total <= 0) {
      error([3], `Media Spend total for ${medium[0]} must be positive.`)
    }
  }

  let totalKpi = 0
  for (const value of form.kpi.data[0].slice(2)) {
    const val = Number(value)
    totalKpi += val
    if (val < 0) {
      error([6], 'KPI values must be non-negative.')
      break
    }
  }
  if (totalKpi <= 0) {
    error([6], 'KPI total must be positive.')
  }
  for (const [index, item] of form.saturation.data.entries()) {
    if (index % 2 === 1) {
      for (const value of item.slice(4)) {
        if (Number(value) <= 0) {
          error([5], 'Saturation values must be positive.')
          break
        }
      }
    }
  }

  if (form.reference.media.length === 0) {
    error([2], 'The model must have Own Controlable Media')
  }

  return { errors: errors, warnings: warnings }
}

export { MONTH_KEYS }
